/**
 * Release and build identity shown as `<release> · <hash>` on login,
 * pending-approval, Settings → About, Copy diagnostics, and Administration →
 * Deploy identity.
 *
 * - Release: `CRUCIBLE_RELEASE` (`0.1.YYYYMMDD.N`), an App Service setting that
 *   scripts/ops/deploy.sh stamps on every deploy. It is not slot-sticky, so it
 *   swaps into production with the slot, and it is read on the server at
 *   request time (never baked into the bundle). Local runs have none: `dev`.
 * - Build: `NEXT_PUBLIC_BUILD_HASH`, the short git SHA baked into the bundle.
 * - Slot: deploy.sh stamps a slot-sticky `CRUCIBLE_SLOT=staging` on the staging
 *   frontend, so a staged release can say it is not yet in production.
 *
 * `frontend/package.json` version is a semver placeholder npm requires; it is
 * not displayed anywhere.
 */

export const LOCAL_RELEASE = 'dev'

type Env = Record<string, string | undefined>

/** Runtime release stamp (server only — reads the App Service setting). */
export function readRelease(env: Env = process.env): string {
  return env.CRUCIBLE_RELEASE?.trim() || LOCAL_RELEASE
}

/**
 * True on a deployment slot that is not production. `CRUCIBLE_SLOT` is the
 * deploy stamp; the `-staging` App Service hostname is the fallback for a slot
 * that predates it.
 */
export function isStagingSlot(env: Env = process.env): boolean {
  const slot = env.CRUCIBLE_SLOT?.trim().toLowerCase()
  if (slot) return slot !== 'production'
  return /-staging\.azurewebsites\.net$/i.test(env.WEBSITE_HOSTNAME?.trim() ?? '')
}

/** Per-build identity (short git SHA baked into the client bundle). */
export function getBuildHash(): string {
  return process.env.NEXT_PUBLIC_BUILD_HASH || LOCAL_RELEASE
}

/** `<release> · <hash>` — middle dot, as everywhere the identity is shown. */
export function formatVersionLabel(release: string, buildHash: string = getBuildHash()): string {
  return `${release} · ${buildHash}`
}
